import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { homePageItems } from '../../../../services/api/home-page-items'
import { nonprofitsApi } from '../../../../services/api/nonprofits'
import { useMainPageStatsSectionStyles } from './main-page-stats-section.styles'
import { MainPageStatAsyncCountCell } from './main-page-stat-async-count-cell'
import { StatsSlot } from './main-page-stats-shared'

/** Count = totalPages × pageSize; same filters for both sold modes. */
const LISTED_ITEMS_STATS_QUERY_BASE = {
  condition: 0,
  priceMin: 0,
  priceMax: 200000,
  pageSize: 12,
  pageIndex: 0,
  searchText: '',
} as const

function listedItemsCountFromResponse(data: {
  totalPages?: number
  pageSize?: number
}): number {
  const totalPages = Number(data.totalPages) || 0
  const pageSize = Number(data.pageSize) || 0
  return totalPages * pageSize
}

export const MainPageStatsSection: React.FC = () => {
  const classes = useMainPageStatsSectionStyles()
  const { t, i18n } = useTranslation()
  /** null = loading; -1 = error (i18n fallback); >=0 = API array length */
  const [nonprofitLookupCount, setNonprofitLookupCount] = useState<number | null>(
    null
  )
  const [categoriesLookupCount, setCategoriesLookupCount] = useState<
    number | null
  >(null)
  const [listedItemsNoSold, setListedItemsNoSold] = useState<number | null>(
    null
  )
  const [listedItemsWithSold, setListedItemsWithSold] = useState<number | null>(
    null
  )

  const slots = useMemo((): StatsSlot[] => {
    const data = t('mainPage.stats.slots', { returnObjects: true })
    return Array.isArray(data) ? (data as StatsSlot[]) : []
  }, [t, i18n.language])

  useEffect(() => {
    let cancelled = false
    nonprofitsApi
      .lookupData({
        pageIndex: '1',
        pageSize: '1',
        categoryId: '',
        searchName: '',
      })
      .then((data) => {
        if (cancelled) return
        setNonprofitLookupCount(Array.isArray(data) ? data.length : 0)
      })
      .catch(() => {
        if (!cancelled) setNonprofitLookupCount(-1)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    nonprofitsApi
      .categories()
      .then((data) => {
        if (cancelled) return
        setCategoriesLookupCount(Array.isArray(data) ? data.length : 0)
      })
      .catch(() => {
        if (!cancelled) setCategoriesLookupCount(-1)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const qFalse = {
      ...LISTED_ITEMS_STATS_QUERY_BASE,
      showSold: false,
    }
    const qTrue = {
      ...LISTED_ITEMS_STATS_QUERY_BASE,
      showSold: true,
    }
    homePageItems
      .availableListedItems(qFalse)
      .then((data) => {
        if (cancelled) return
        setListedItemsNoSold(listedItemsCountFromResponse(data))
      })
      .catch(() => {
        if (!cancelled) setListedItemsNoSold(-1)
      })
    homePageItems
      .availableListedItems(qTrue)
      .then((data) => {
        if (cancelled) return
        setListedItemsWithSold(listedItemsCountFromResponse(data))
      })
      .catch(() => {
        if (!cancelled) setListedItemsWithSold(-1)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const listedSoldDiff = useMemo(() => {
    if (listedItemsNoSold === null || listedItemsWithSold === null) return null
    if (listedItemsNoSold === -1 || listedItemsWithSold === -1) return -1
    return Math.abs(listedItemsNoSold - listedItemsWithSold)
  }, [listedItemsNoSold, listedItemsWithSold])

  const targets = useMemo(() => {
    const first =
      nonprofitLookupCount === null
        ? null
        : nonprofitLookupCount === -1
          ? slots[0]?.target ?? 0
          : nonprofitLookupCount
    const second =
      categoriesLookupCount === null
        ? null
        : categoriesLookupCount === -1
          ? slots[1]?.target ?? 0
          : categoriesLookupCount
    const third =
      listedItemsNoSold === null
        ? null
        : listedItemsNoSold === -1
          ? slots[2]?.target ?? 0
          : listedItemsNoSold
    const fourth =
      listedSoldDiff === null
        ? null
        : listedSoldDiff === -1
          ? slots[3]?.target ?? 0
          : listedSoldDiff
    return slots.map((s, i) => {
      if (i === 0) return first
      if (i === 1) return second
      if (i === 2) return third
      if (i === 3) return fourth
      return s.target
    })
  }, [
    nonprofitLookupCount,
    categoriesLookupCount,
    listedItemsNoSold,
    listedSoldDiff,
    slots,
  ])

  const slot0 = slots[0]
  const raw0 = targets[0]
  const effectiveTarget0 =
    raw0 === null ? 0 : (raw0 ?? slot0?.target ?? 0)

  const slot1 = slots[1]
  const raw1 = targets[1]
  const effectiveTarget1 =
    raw1 === null ? 0 : (raw1 ?? slot1?.target ?? 0)

  const slot2 = slots[2]
  const raw2 = targets[2]
  const effectiveTarget2 =
    raw2 === null ? 0 : (raw2 ?? slot2?.target ?? 0)

  const slot3 = slots[3]
  const raw3 = targets[3]
  const effectiveTarget3 =
    raw3 === null ? 0 : (raw3 ?? slot3?.target ?? 0)

  return (
    <section className={classes.root} aria-label={t('mainPage.stats.ariaLabel')}>
      <div className={classes.row}>
        <MainPageStatAsyncCountCell
          tone="cell0"
          slot={slot0}
          asyncCount={nonprofitLookupCount}
          effectiveTarget={effectiveTarget0}
          label={slot0?.label ?? t('mainPage.stats.slots.0.label')}
          loadingAriaLabelKey="mainPage.stats.loadingNonprofitCount"
          t={t}
        />
        <MainPageStatAsyncCountCell
          tone="cell1"
          slot={slot1}
          asyncCount={categoriesLookupCount}
          effectiveTarget={effectiveTarget1}
          label={slot1?.label ?? t('mainPage.stats.slots.1.label')}
          loadingAriaLabelKey="mainPage.stats.loadingCategoriesCount"
          t={t}
        />
        <MainPageStatAsyncCountCell
          tone="cell2"
          slot={slot2}
          asyncCount={listedItemsNoSold}
          effectiveTarget={effectiveTarget2}
          label={slot2?.label ?? t('mainPage.stats.slots.2.label')}
          loadingAriaLabelKey="mainPage.stats.loadingListedItemsCount"
          t={t}
        />
        <MainPageStatAsyncCountCell
          tone="cell3"
          slot={slot3}
          asyncCount={listedSoldDiff}
          effectiveTarget={effectiveTarget3}
          label={slot3?.label ?? t('mainPage.stats.slots.3.label')}
          loadingAriaLabelKey="mainPage.stats.loadingListedItemsDiffCount"
          t={t}
        />
      </div>
    </section>
  )
}
